/* eslint-disable spire/export-styles */
import colorPallete, { bodyFonts, printFonts } from "@DSG/MobiusOverrides/styles/variables";
import { labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    unitOfMeasure?: string;
};
export interface ProductPriceSavingsMessageStyles {
    saveMessage?: InjectableCss;
    regularMessage?: InjectableCss;
    wrapper?: InjectableCss;
}

export const productPriceSavingsMessageStyles: ProductPriceSavingsMessageStyles = {
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 8px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    regularMessage: {
        css: css`
            font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize} /
                ${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
            color: ${colorPallete.grey600};
            .strike-thorough {
                position: relative;
                &:after {
                    border-top: 1px solid ${colorPallete.grey600};
                    content: "";
                    height: 1px;
                    width: 100%;
                    position: absolute;
                    left: 0;
                    top: 50%;
                }
            }
            @media print {
                font-size: ${printFonts.bodyRegularSm.fontSize};
                line-height: ${printFonts.bodyRegularSm.lineHeight};
            }
        `,
    },
    saveMessage: {
        css: css`
            &:not(:empty) {
                background: ${colorPallete.saveSemanticGreen50};
                color: ${colorPallete.saveSemanticGreen500};
                padding: 6px 10px;
                min-height: 30px;
                border-radius: 6px;
                margin-right: 8px;
                display: inline-flex;
                font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize} /
                    ${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
                @media print {
                    font-size: ${printFonts.bodyRegularSm.fontSize};
                    line-height: ${printFonts.bodyRegularSm.lineHeight};
                }
            }
        `,
    },
};

const styles = productPriceSavingsMessageStyles;
const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    unitOfMeasure,
    ...otherProps
}) => {
    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    }

    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);

    let message = "";
    let saveMessage = "";
    let strikethoroughClass = "";

    if (showSavingsAmount && showSavingsPercent) {
        saveMessage = translate("Save {0}%", `${savingsPercent}`);
        message = translate(
            "{0}/{1}",
            unitListPriceDisplay,
            pricing?.additionalResults?.realTimeUnitOfMeasure ||
                pricing?.unitOfMeasure ||
                unitOfMeasure ||
                (pricing.unitOfMeasure !== null && pricing.unitOfMeasure !== undefined ? pricing.unitOfMeasure : ""),
        );
        strikethoroughClass = "strike-thorough";
    } else if (showSavingsPercent) {
        saveMessage = translate("Save {0}%", `${savingsPercent}`);
        message = translate(
            "{0}/{1}",
            unitListPriceDisplay,
            pricing?.additionalResults?.realTimeUnitOfMeasure ||
                pricing?.unitOfMeasure ||
                unitOfMeasure ||
                (pricing.unitOfMeasure !== null && pricing.unitOfMeasure !== undefined ? pricing.unitOfMeasure : ""),
        );
        strikethoroughClass = "strike-thorough";
    } else if (showSavingsAmount) {
        saveMessage = translate("Save {0}", localizedSavingsAmount);
        message = translate(
            "{0}/{1}",
            unitListPriceDisplay,
            pricing?.additionalResults?.realTimeUnitOfMeasure ||
                pricing?.unitOfMeasure ||
                unitOfMeasure ||
                (pricing.unitOfMeasure !== null && pricing.unitOfMeasure !== undefined ? pricing.unitOfMeasure : ""),
        );
        strikethoroughClass = "strike-thorough";
    } else {
        message = translate(
            "{0}/{1}",
            unitListPriceDisplay,
            pricing?.additionalResults?.realTimeUnitOfMeasure ||
                pricing?.unitOfMeasure ||
                unitOfMeasure ||
                (pricing.unitOfMeasure !== null && pricing.unitOfMeasure !== undefined ? pricing.unitOfMeasure : ""),
        );
        strikethoroughClass = "strike-thorough";
    }

    return (
        <Typography {...otherProps} data-test-selector="productPriceSavingsMessage" {...styles.wrapper}>
            <Typography {...styles.saveMessage} className="discounted-price">
                {saveMessage}
            </Typography>
            <Typography className="regular-price"  {...styles.regularMessage}>
                <Typography className="reg-label">
                    {translate('reg ')}
                </Typography>
                <Typography className={strikethoroughClass}>
                    {message}
                </Typography>
            </Typography>
        </Typography>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
